import { Sliders, DatabaseZap, ShieldCheck, Factory, Briefcase, Package, Tag, Percent, Printer, Truck, AlertTriangle, UserCheck, Tags, BadgePercent, HandCoins, TrendingUp, Rocket, LifeBuoy, Cog, Lock, Plug, Receipt, Layers, Calculator } from 'lucide-react';
import { Landmark, KeyRound, UsersRound, Activity, ScrollText, FileImage, BellRing, FileCheck2, Boxes, Banknote, ClipboardCheck, Webhook, BarChart3, BookOpenCheck, Scale, ReceiptText, ShoppingCart, FileText, Handshake, Inbox, LayoutDashboard, Settings, Users, type LucideIcon, PieChart, Repeat, Building2, Wallet } from 'lucide-react';

/** `search`: query parameters of the link (e.g. ?type=INVOICE); such a link is highlighted only on that exact variant. */
/** `module`: optional module the entry belongs to; hidden when the company's business profile leaves it out (comfort only). */
export type ModuleKey = 'salesAdvanced' | 'pos' | 'purchasing' | 'inventory' | 'manufacturing' | 'collections' | 'bank' | 'einvoice' | 'analytic' | 'fx' | 'assets' | 'projects' | 'crm' | 'integrations';
export interface NavItem { to: string; label: string; icon: LucideIcon; permission?: string; badge?: 'inbox'; search?: Record<string, string>; module?: ModuleKey }
/** A folded set of related entries inside a group (e.g. pricing under Sales). */
export interface NavSub { label: string; icon: LucideIcon; items: NavItem[] }
export type NavEntry = NavItem | NavSub;
export interface NavGroup { label: string; items: NavEntry[] }

export const isSub = (e: NavEntry): e is NavSub => 'items' in e;
/** Every link of a group, sub-menus included. */
export const groupLinks = (g: { items: NavEntry[] }): NavItem[] => g.items.flatMap((e) => (isSub(e) ? e.items : [e]));

/** Entries are shown only when the user holds the module's view permission. Hiding is comfort: the server enforces access. */
export const NAV: NavGroup[] = [
  { label: 'nav.overview', items: [
    { to: '/', label: 'nav.dashboard', icon: LayoutDashboard },
    { to: '/inbox', label: 'nav.inbox', icon: Inbox, badge: 'inbox' },
    { to: '/workflow', label: 'nav.workflow', icon: ClipboardCheck },
    { to: '/onboarding', label: 'nav.onboarding', icon: Rocket, permission: 'settings.view' },
  ] },
  { label: 'nav.sales', items: [
    { to: '/sales', label: 'nav.invoices', icon: FileText, permission: 'invoice.view' },
    { to: '/pos', label: 'pos.nav', icon: ShoppingCart, permission: 'pos.view', module: 'pos' },
    { to: '/sales/customer-claims', label: 'nav.customerClaims', icon: AlertTriangle, permission: 'crm.claim.view' },
    { label: 'nav.sub.billing', icon: Layers, items: [
      { to: '/sales/grouped-invoicing', label: 'nav.groupedInvoicing', icon: FileCheck2, permission: 'invoice.create', module: 'salesAdvanced' },
      { to: '/recurring-invoices', label: 'nav.recurringInvoices', icon: Repeat, permission: 'invoice.view', module: 'salesAdvanced' },
      { to: '/sales/contracts', label: 'nav.salesContracts', icon: ScrollText, permission: 'contract.view', module: 'salesAdvanced' },
    ] },
    { label: 'nav.sub.pricing', icon: Tag, items: [
      { to: '/customer-categories', label: 'nav.customerCategories', icon: Tag, permission: 'pricelist.view', module: 'salesAdvanced' },
      { to: '/discount-groups', label: 'nav.discountGroups', icon: Percent, permission: 'pricelist.view', module: 'salesAdvanced' },
      { to: '/sales/promotions', label: 'nav.promotions', icon: BadgePercent, permission: 'sales.view', module: 'salesAdvanced' },
      { to: '/price-update', label: 'nav.priceWizard', icon: TrendingUp, permission: 'pricelist.manage', module: 'salesAdvanced' },
      { to: '/sales/commissions', label: 'nav.commissions', icon: HandCoins, permission: 'sales.view', module: 'salesAdvanced' },
    ] },
  ] },
  { label: 'nav.masterData', items: [
    { to: '/parties', label: 'nav.parties', icon: Users, permission: 'party.view' },
    { to: '/kyc', label: 'nav.kyc', icon: ShieldCheck, permission: 'party.kyc.request' },
    { to: '/products', label: 'nav.products', icon: Package, permission: 'product.view' },
    { to: '/product-requests', label: 'nav.productRequests', icon: ClipboardCheck, permission: 'catalog.product.request' },
  ] },
  { label: 'nav.crm', items: [
    { to: '/crm', label: 'nav.pipeline', icon: Handshake, permission: 'crm.view', module: 'crm' },
    { to: '/projects', label: 'nav.projects', icon: Briefcase, permission: 'project.view', module: 'projects' },
  ] },
  { label: 'nav.purchasing', items: [
    { to: '/purchasing', label: 'nav.purchases', icon: ShoppingCart, permission: 'purchase.view', module: 'purchasing' },
    { to: '/sourcing', label: 'sourcing.nav', icon: ClipboardCheck, permission: 'sourcing.view', module: 'purchasing' },
    { to: '/purchasing/registers', label: 'proc.registersNav', icon: ScrollText, permission: 'purchase.register.view', module: 'purchasing' },
  ] },
  { label: 'nav.stock', items: [
    { to: '/inventory', label: 'nav.inventory', icon: Boxes, permission: 'inventory.view', module: 'inventory' },
    { to: '/inventory/bins', label: 'whops.nav', icon: Package, permission: 'inventory.view', module: 'inventory' },
    { to: '/inventory/revaluations', label: 'nav.revaluations', icon: Scale, permission: 'stock.revaluation.view', module: 'inventory' },
    { to: '/manufacturing', label: 'mfg.nav', icon: Factory, permission: 'mfg.view', module: 'manufacturing' },
  ] },
  { label: 'nav.money', items: [
    { to: '/payments', label: 'nav.payments', icon: Banknote, permission: 'payment.view' },
    { to: '/treasury', label: 'nav.treasury', icon: Wallet, permission: 'treasury.view' },
    { to: '/collections', label: 'collections.nav', icon: Banknote, permission: 'instrument.view', module: 'collections' },
    { to: '/bank', label: 'nav.bank', icon: Landmark, permission: 'bank.reconcile', module: 'bank' },
  ] },
  { label: 'nav.accounting', items: [
    { to: '/accounting', label: 'nav.ledger', icon: BookOpenCheck, permission: 'accounting.view' },
    { to: '/analytic', label: 'analytic.nav', icon: PieChart, permission: 'analytic.view', module: 'analytic' },
    { to: '/assets', label: 'nav.assets', icon: Building2, permission: 'asset.view', module: 'assets' },
    { to: '/fx', label: 'nav.fx', icon: Banknote, permission: 'fx.view', module: 'fx' },
    { label: 'nav.sub.tax', icon: Receipt, items: [
      { to: '/vat', label: 'nav.vat', icon: Scale, permission: 'vat.view' },
      { to: '/withholding', label: 'nav.withholding', icon: ReceiptText, permission: 'withholding.view' },
      { to: '/einvoice', label: 'nav.einvoice', icon: FileCheck2, permission: 'einvoice.view', module: 'einvoice' },
      { to: '/fiscal', label: 'nav.fiscal', icon: Calculator, permission: 'fiscal.rule.view' },
    ] },
  ] },
  { label: 'nav.insights', items: [
    { to: '/reports', label: 'nav.reports', icon: BarChart3, permission: 'invoice.view' },
  ] },
  { label: 'nav.settings', items: [
    { to: '/company', label: 'nav.company', icon: Building2, permission: 'company.view' },
    { label: 'nav.sub.documents', icon: FileImage, items: [
      { to: '/document-settings', label: 'nav.documentSettings', icon: FileImage, permission: 'document.manage' },
      { to: '/settings/series', label: 'nav.series', icon: ScrollText, permission: 'series.manage' },
      { to: '/settings/print-layouts', label: 'nav.printLayouts', icon: Printer, permission: 'settings.view' },
      { to: '/settings/standard-texts', label: 'nav.standardTexts', icon: FileText, permission: 'settings.view' },
      { to: '/settings/custom-fields', label: 'nav.customFields', icon: Settings, permission: 'customfield.manage' },
      { to: '/settings/sub-statuses', label: 'nav.subStatuses', icon: Tags, permission: 'sales.status.manage' },
    ] },
    { label: 'nav.sub.rules', icon: Cog, items: [
      { to: '/settings/parameters', label: 'nav.parameters', icon: Sliders, permission: 'parameter.view' },
      { to: '/settings/alert-rules', label: 'nav.alertRules', icon: AlertTriangle, permission: 'settings.view' },
      { to: '/notifications', label: 'nav.notifications', icon: BellRing, permission: 'notification.manage' },
      { to: '/settings/shipping', label: 'nav.shipping', icon: Truck, permission: 'settings.view' },
      { to: '/settings/user-defaults', label: 'nav.userDefaults', icon: UserCheck },
    ] },
    { label: 'nav.sub.access', icon: Lock, items: [
      { to: '/users', label: 'nav.users', icon: UsersRound, permission: 'user.manage' },
      { to: '/settings/support-access', label: 'nav.supportAccess', icon: LifeBuoy, permission: 'support.grant' },
      { to: '/audit', label: 'nav.audit', icon: ScrollText, permission: 'audit.view' },
    ] },
    { label: 'nav.sub.system', icon: Plug, items: [
      { to: '/settings/migration', label: 'nav.migration', icon: DatabaseZap, permission: 'party.manage' },
      { to: '/webhooks', label: 'nav.webhooks', icon: Webhook, permission: 'integration.view', module: 'integrations' },
      { to: '/api-keys', label: 'nav.apiKeys', icon: KeyRound, permission: 'apikey.manage', module: 'integrations' },
      { to: '/platform', label: 'nav.platform', icon: Activity, permission: 'outbox.manage' },
    ] },
  ] },
];

export const MOBILE_TABS: NavItem[] = [
  { to: '/', label: 'nav.dashboardShort', icon: LayoutDashboard },
  { to: '/sales', label: 'nav.invoices', icon: FileText, permission: 'invoice.view' },
  { to: '/payments', label: 'nav.payments', icon: Banknote, permission: 'payment.view' },
  { to: '/inbox', label: 'nav.inboxShort', icon: Inbox, badge: 'inbox' },
  { to: '/account', label: 'nav.more', icon: Settings },
];
